import React, { useEffect, useLayoutEffect, useState } from "react";
import useChart from "../../Components/Chart/useChart";
import Chart from "../../Components/Chart/Chart";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import { SLA_STATUS_NA } from "./SLA/SLAUtils";
import useSummary from "../useSummary";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import am4themes_spiritedaway from "@amcharts/amcharts4/themes/spiritedaway";
import useCustomWidgetClick from "../CustomWidget/CustomWidgetChartTypes/useCustomWidgetClick";
import type { SeriesHitEvent } from "./BarChartUtils";
import "./piechart.scss";

am4core.useTheme(am4themes_animated);
am4core.useTheme(am4themes_spiritedaway);

const DEFAULT_MAX_CATEGORIES = 6;

interface PieChartCategory {
  title?: string;
  fill?: string;
  sort?: number;
}

interface PieChartFiltersConfig {
  filtersjson?: string | null;
  scopesjson?: string | null;
  scopesid?: string | null;
  filters_fields_to_override?: string[];
}

interface PieChartDataItem {
  category?: string;
  value?: number;
  fill?: string;
  sort?: number;
}

export interface PieChartProps {
  chartName: string;
  legendTitle?: string;
  filtersConfig?: PieChartFiltersConfig;
  selection?: string[];
  groupBy?: string[];
  maxCategories?: number;
  categories?: Record<string, PieChartCategory> | null;
  skip?: boolean;
  actualFilterField?: string;
  dataSubject?: string;
  additionalFiltersInDrilldown?: unknown[];
  allowClick?: boolean;
  setIsReadyToExport?: ((isReady: boolean) => void) | null;
  chartFilterValue?: unknown[]; // passed by several widgets but currently unread by PieChart — kept for runtime parity
}

const PieChart: React.FC<PieChartProps> = ({
  chartName,
  legendTitle,
  filtersConfig,
  selection = ["finding.id"],
  groupBy,
  maxCategories = DEFAULT_MAX_CATEGORIES,
  categories = null,
  skip,
  actualFilterField,
  dataSubject,
  additionalFiltersInDrilldown = [],
  allowClick = true,
  setIsReadyToExport = null,
}) => {
  const [dashboardData, setDashboardData] = useState<PieChartDataItem[] | null>(
    null
  );
  const [otherData, setOtherData] = useState<PieChartDataItem[] | null>(null);

  const { handleFilterClick } = useCustomWidgetClick({
    allowClick,
    filterFields: [actualFilterField],
    categories,
    otherData,
    additionalFiltersInDrilldown,
    filtersConfig: {
      filterToExecute: filtersConfig?.filtersjson,
      filterId: null,
      scopeToExecute: filtersConfig?.scopesjson,
      scopeId: filtersConfig?.scopesid,
      filterFieldsToOverride: filtersConfig?.filters_fields_to_override,
    },
  });

  const {
    summaryData: data,
    summaryDataError,
    summaryLoading,
    refetchSummary,
  } = useSummary({
    variables: {
      name: "FINDINGS_VIEW",
      selection: selection,
      group_by: groupBy,
      aggregate_functions: ["COUNT"],
      round_functions: ["ROUND"],
      filters_config: filtersConfig,
      max_results: maxCategories,
    },
    skip,
  });

  const {
    chartLoading,
    dataError,
    dataExists,
    setChartError,
    setChartDataExists,
    startLoadingChart,
    stopLoadingChart,
    createChart,
  } = useChart(setIsReadyToExport);

  const clearData = () => {
    setDashboardData(null);
  };

  useEffect(() => {
    if (!!summaryLoading) {
      startLoadingChart();
      clearData();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [summaryLoading, setChartDataExists, setChartError, startLoadingChart]);

  useEffect(() => {
    if (!!summaryDataError) {
      setChartError(true);
      stopLoadingChart();
    }
  }, [summaryDataError, setChartError, stopLoadingChart]);

  useEffect(() => {
    if (!!data) {
      // `data` is parsed JSON from useSummary (untyped boundary), an array of
      // grouped tuples shaped [categoryKey, count].
      const summaryRows = data as [string, number][];
      let chartData: PieChartDataItem[];
      if (!!categories) {
        chartData = summaryRows
          ?.filter((slaItem) => slaItem[0] !== SLA_STATUS_NA)
          ?.map((slaItem) => {
            const slaValue = categories[slaItem[0]];
            return {
              category: slaValue?.title,
              value: slaItem?.[1],
              fill: slaValue?.fill,
              sort: slaValue?.sort,
            };
          })
          // Non-null assertions preserve the original arithmetic exactly,
          // including NaN when a `sort` is absent.
          .sort((a, b) => a.sort! - b.sort!);
      } else {
        chartData = summaryRows
          .map((item) => {
            return {
              category: item[0] || "N/A",
              value: item[1],
            };
          })
          .sort((a, b) => b.value! - a.value!);
      }

      if (!chartData.length) {
        stopLoadingChart();
        setChartDataExists(false);
        setOtherData(null);
      } else {
        setChartError(false);
        stopLoadingChart();
        setChartDataExists(true);
        setDashboardData(chartData);
      }
    }
  }, [data, categories]);

  useLayoutEffect(() => {
    if (dataExists) {
      stopLoadingChart();

      let [chart, legendContainer] = createChart(
        chartName,
        am4charts.PieChart,
        { isScrollable: false }
      );

      const pieSeries = chart.series.push(new am4charts.PieSeries());
      pieSeries.dataFields.value = "value";
      pieSeries.dataFields.category = "category";
      if (!!categories) {
        pieSeries.slices.template.propertyFields.fill = "fill";
      }

      pieSeries.colors.list = [
        am4core.color("#E33992"),
        am4core.color("#556FC6"),
        am4core.color("#669f76"),
        am4core.color("#62C6EF"),
        am4core.color("#804DCB"),
        am4core.color("#A31222"),
        am4core.color("#c09ff1"),
        am4core.color("#9b9a9a"),
      ];

      pieSeries.labels.template.disabled = true;
      chart.innerRadius = am4core.percent(55);
      pieSeries.cursorOverStyle = am4core.MouseCursorStyle.pointer;
      pieSeries.slices.template.strokeWidth = 2;
      pieSeries.slices.template.strokeOpacity = 1;

      pieSeries.slices.template.events.on("hit", (e) => {
        // amCharts' hit-event type is wider than the structural shape consumers
        // read; cast to the consumed SeriesHitEvent shape.
        handleFilterClick(e as unknown as SeriesHitEvent);
      });

      // Left-align name and right-align percentage in the label
      chart.legend.labels.template.text = "{category}";
      chart.legend.valueLabels.template.text =
        "{value.percent.formatNumber('#.#')}%";
      chart.legend.labels.template.horizontalCenter = "left";
      chart.legend.valueLabels.template.align = "right";
      chart.legend.valueLabels.template.horizontalCenter = "right";

      // giving more space between category and percentage
      chart.legend.valueLabels.template.dx = 10;

      // Set a fixed width for category labels (without it - the percentages are not aligned nicely)
      chart.legend.labels.template.maxWidth = 40;

      pieSeries.slices.template.tooltipHTML = `
        <div class="pie-chart-tooltip-container">
          <span class="pie-chart-tooltip-title">{category}</span><br />
          {value} ({value.percent.formatNumber("#.0")}%)
        </div>
      `;
      chart.legend.useDefaultMarker = true;
      let markerTemplate = chart.legend.markers.template;
      markerTemplate.width = 12;
      markerTemplate.height = 12;
      markerTemplate.strokeOpacity = 0;

      chart.legend.data = chart.data;
      const title = chart.legend.createChild(am4core.Label);
      title.text = `[font-weight:500]${legendTitle}[/]`;
      title.dx = 10;
      title.dy = -8;
      title.wrap = true;
      chart.legend.labels.push(title);
      chart.legend.align = "left";
      chart.legend.valign = "middle";
      chart.legend.contentAlign = "left";
      chart.legend.contentValign = "top";
      chart.legend.maxColumns = 1;
      chart.legend.paddingTop = 0;
      chart.legend.labels.template.truncate = true;
      chart.legend.labels.template.wrap = true;
      chart.legend.itemContainers.template.paddingTop = 3;

      chart.legend.itemContainers.template.adapter.add(
        "paddingTop",
        (paddingTop, target) => {
          if (target.dataItem!.index === 0) {
            return 0;
          }
          return paddingTop;
        }
      );

      // When `dataExists` is true, `dashboardData` has been set to a non-empty
      // array; assert non-null to satisfy amcharts' `data: any[]` setter while
      // preserving the original assigned value.
      chart.data = dashboardData as PieChartDataItem[];
      chart.paddingBottom = 0;
      chart.paddingTop = 0;

      const label = pieSeries.createChild(am4core.Label);
      label.text = `[font-weight:500 font-size:32px]{values.value.sum.formatNumber("#.#a")}[/]\n[font-size:14px]${dataSubject}[/]`;
      label.horizontalCenter = "middle";
      label.verticalCenter = "middle";
      label.textAlign = "middle";

      return () => {
        chart.dispose();
        legendContainer?.dispose();
      };
    }
  }, [
    dashboardData,
    dataExists,
    otherData,
    allowClick,
    actualFilterField,
    dataSubject,
    createChart,
  ]);

  return (
    <Chart
      chartLoading={chartLoading}
      chartName={chartName}
      chartError={dataError}
      chartData={dataExists}
      chartReload={refetchSummary}
    />
  );
};

export default PieChart;
